/** The Communication lane's time axis (plan §1.1, phase 4): one horizontal axis from the
 *  booking to a week after the trip, the spine's five stages as its bands (each with its
 *  own scale — `timelineModel`), and a lane of dots per kind: feed imports, changes and
 *  console audit rows, deliveries, planned notifications (TBEL v2 tenants only — a v1
 *  tenant has none, and the lane says so), the trip itself.
 *
 *  - **A pending date/time change** (edit mode) shows the departure moving — the stored
 *    trip dashed, the moved one amber with where it goes — and marks every planned
 *    notification not yet sent "will be replanned", in words as well as colour.
 *  - **Click a dot** → its row: an import on the Integration tab, a delivery or a change
 *    on the Activity tab (`TimelineNavContext`, provided by the page); an audit row opens
 *    the audit log entry. Planned rows and the anchors carry their details only.
 *  - **Keyboard:** the dots are one tab stop; ←/→ move through them in time order,
 *    Home/End to the first/last; each has a text label (and its details as a tooltip on
 *    focus). **Show as a list** gives the same items as an ordered list — the reading for
 *    screen readers, and for anyone who wants the text.
 *  - **Honest states:** a source the server could not read in full (the item cap, the
 *    31-day delivery budget, a timeout) is hatched where it is missing and named under the
 *    axis, with **Load earlier**; loading is a skeleton, a failure says so.
 *
 *  Times in the tour's zone, labelled once. */
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha, Alert, Box, Button, ButtonBase, Collapse, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { bookingActionKeys, fetchBookingTimeline } from '@/api/bookingActions';
import { useDraft } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { BlockRule } from '@/features/records/panels/BlockRule';
import { hatchSx, NOW_RULE_SX, timeMarkSx } from '@/features/records/timeline/marks';
import { axisPct } from '@/features/records/timeline/scale';
import { consoleRouteFor } from '@/utils/gridLinks';
import { formatZoned, parseWallClock, zonedWallClockToInstant } from '@/utils/zonedTime';

import {
  AXIS_LANES,
  earlierWindow,
  mergeTimelinePages,
  timelineModel,
  type AxisDot,
  type AxisLane,
  type AxisModel,
  type AxisTarget,
} from '../views/timelineModel';

/** Where a dot's click goes — the page's tabs (absent: dots carry details only). */
export interface TimelineNav {
  onShow: (target: Exclude<AxisTarget, { tab: 'audit' }>) => void;
}

export const TimelineNavContext = createContext<TimelineNav | null>(null);

const LABEL_PX = 72;
const LANE_PX = 26;
/** The trip lane while a move is pending: the stored trip above, the moved one below. */
const MOVE_LANE_PX = 44;

function laneHeight(lane: AxisLane, model: AxisModel): number {
  return lane === 'trip' && model.move ? MOVE_LANE_PX : LANE_PX;
}
const BAND_HEAD_PX = 20;
const TICKS_PX = 20;

const FOCUS_RING = { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2, borderRadius: '4px' } as const;

function Dot({
  dot,
  index,
  active,
  register,
  onFocusIndex,
  nav,
  top = '50%',
}: {
  dot: AxisDot;
  index: number;
  active: boolean;
  register: (i: number, el: HTMLElement | null) => void;
  onFocusIndex: (i: number) => void;
  nav: TimelineNav | null;
  /** Vertical centre in its lane (the trip's upper row while a move is pending). */
  top?: string;
}) {
  const title = (
    <Box sx={{ display: 'grid', gap: 0.25 }}>
      <span>{dot.text}</span>
      {dot.detail && <span>{dot.detail}</span>}
    </Box>
  );
  const common = {
    ref: (el: HTMLElement | null) => register(index, el),
    tabIndex: active ? 0 : -1,
    'aria-label': dot.text,
    'data-dot': dot.item.kind,
    'data-tone': dot.tone,
    'data-index': index,
    onFocus: () => onFocusIndex(index),
    sx: {
      position: 'absolute',
      left: axisPct(dot.x),
      top,
      transform: 'translate(-50%, -50%)',
      width: 18,
      height: 18,
      display: 'grid',
      placeItems: 'center',
      p: 0,
      borderRadius: '4px',
      zIndex: 1,
      '&:focus-visible, &.Mui-focusVisible': FOCUS_RING,
      '&:hover': { zIndex: 2 },
    },
  } as const;
  const mark = <Box component="span" aria-hidden sx={timeMarkSx(dot.item.kind, dot.tone)} />;
  const target = dot.target;

  let el: ReactNode;
  if (target?.tab === 'audit') {
    el = (
      <ButtonBase
        {...common}
        component={RouterLink}
        to={consoleRouteFor('console_audit_log', target.auditId) ?? '/audit'}
        target="_blank"
        rel="noopener"
      >
        {mark}
      </ButtonBase>
    );
  } else if (target && nav) {
    el = (
      <ButtonBase {...common} onClick={() => nav.onShow(target)}>
        {mark}
      </ButtonBase>
    );
  } else {
    el = (
      <Box component="span" role="img" {...common}>
        {mark}
      </Box>
    );
  }
  return (
    <Tooltip title={title} describeChild placement="top">
      {el as ReactElement}
    </Tooltip>
  );
}

function Lane({
  lane,
  model,
  children,
}: {
  lane: AxisLane;
  model: AxisModel;
  children?: ReactNode;
}) {
  const gap = model.gaps.find((g) => g.lane === lane && g.coveredX !== null);
  return (
    <Box data-lane={lane} sx={{ position: 'relative', height: laneHeight(lane, model), borderBottom: 1, borderColor: 'divider' }}>
      {gap && gap.coveredX !== null && gap.coveredX > 0 && (
        <Box
          aria-hidden
          data-gap={lane}
          sx={(t) => ({
            position: 'absolute',
            left: 0,
            width: axisPct(gap.coveredX!),
            top: 4,
            bottom: 4,
            borderRadius: '3px',
            ...hatchSx(t),
          })}
        />
      )}
      {children}
    </Box>
  );
}

function TripLane({ model }: { model: AxisModel }) {
  const { trip, move } = model;
  if (!trip) return null;
  // While a move is pending the stored trip keeps the lane's upper row and the moved
  // one takes the lower, with where it goes — nothing overlaps.
  const bar = (x0: number, x1: number, top: string) => ({
    position: 'absolute' as const,
    left: axisPct(x0),
    width: `max(6px, ${axisPct(Math.max(0, x1 - x0))})`,
    top,
    height: 8,
    transform: 'translateY(-50%)',
    borderRadius: '4px',
  });
  const upper = move ? '13px' : '50%';
  const lower = '31px';
  return (
    <>
      <Box
        aria-hidden
        data-trip={move ? 'stored' : 'trip'}
        sx={(t) => ({
          ...bar(trip.x0, trip.x1, upper),
          ...(move
            ? { border: `1px dashed ${alpha(t.palette.primary.main, 0.6)}`, bgcolor: 'transparent' }
            : { bgcolor: t.palette.primary.main }),
        })}
      />
      {move && (
        <>
          <Box aria-hidden data-trip="moved" sx={(t) => ({ ...bar(move.to.x0, move.to.x1, lower), bgcolor: t.palette.warning.main })} />
          <Typography
            variant="caption"
            data-move-label
            sx={{
              position: 'absolute',
              top: lower,
              transform: move.to.x1 > 0.8 ? 'translate(-100%, -50%)' : 'translateY(-50%)',
              left: move.to.x1 > 0.8 ? `calc(${axisPct(move.to.x0)} - 8px)` : `calc(${axisPct(move.to.x1)} + 8px)`,
              whiteSpace: 'nowrap',
              color: 'warning.dark',
              fontWeight: 600,
              bgcolor: 'background.paper',
              px: 0.5,
              lineHeight: 1.4,
            }}
          >
            moves to {move.label}
          </Typography>
        </>
      )}
    </>
  );
}

/** The marks in use: every kind, and a variant (failed, sent, replanned) only when a dot
 *  shows it. */
function Legend({ dots }: { dots: AxisDot[] }) {
  const has = (tone: AxisDot['tone'], kind?: AxisDot['item']['kind']) =>
    dots.some((d) => d.tone === tone && (!kind || d.item.kind === kind));
  const item = (kind: AxisDot['item']['kind'], tone: AxisDot['tone'], label: string) => (
    <Box component="li" key={label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box component="span" aria-hidden sx={timeMarkSx(kind, tone)} />
      {label}
    </Box>
  );
  return (
    <Box
      component="ul"
      aria-label="Legend"
      sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '4px 16px', typography: 'caption', color: 'text.secondary' }}
    >
      {item('import', 'default', 'Feed import')}
      {item('change', 'default', 'Change')}
      {item('audit', 'default', 'Console action')}
      {item('delivery', 'default', 'Delivered')}
      {has('error', 'delivery') && item('delivery', 'error', 'Delivery failed')}
      {has('dropped', 'delivery') && item('delivery', 'dropped', 'Captured (sandbox)')}
      {item('planned', 'default', 'Planned')}
      {has('done') && item('planned', 'done', 'Planned, sent')}
      {has('dropped', 'planned') && item('planned', 'dropped', 'Planned, skipped')}
      {has('replan') && item('planned', 'replan', 'Will be replanned')}
    </Box>
  );
}

function targetText(t: AxisTarget | null): string | null {
  if (!t) return null;
  if (t.tab === 'integration') return 'Show in Integration';
  if (t.tab === 'audit') return 'Open the audit entry';
  return 'Show in Activity';
}

function ItemList({ dots, nav, tz }: { dots: AxisDot[]; nav: TimelineNav | null; tz: string }) {
  return (
    <Box component="ol" aria-label="Timeline as a list" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 0.5 }} data-timeline-list>
      {dots.map((d) => {
        const action = targetText(d.target);
        const target = d.target;
        return (
          <Box component="li" key={d.key} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', typography: 'body2', flexWrap: 'wrap' }}>
            <Box component="time" dateTime={d.item.ts} sx={{ flex: 'none', minWidth: 132, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {formatZoned(d.item.ts, tz, { zone: false })}
            </Box>
            <Box component="span" aria-hidden sx={{ display: 'inline-grid', placeItems: 'center', width: 14, flex: 'none', alignSelf: 'center' }}>
              <Box component="span" sx={timeMarkSx(d.item.kind, d.tone)} />
            </Box>
            <span>
              {d.text.split(' · ').slice(0, 2).join(' · ')}
              {d.detail ? (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {' '}
                  · {d.detail}
                </Box>
              ) : null}
            </span>
            {action && target?.tab === 'audit' && (
              <Link
                component={RouterLink}
                to={consoleRouteFor('console_audit_log', target.auditId) ?? '/audit'}
                target="_blank"
                rel="noopener"
                underline="hover"
              >
                {action}
              </Link>
            )}
            {action && target && target.tab !== 'audit' && nav && (
              <Link component="button" type="button" underline="hover" onClick={() => nav.onShow(target)} sx={{ typography: 'body2' }}>
                {action}
              </Link>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/** The draft's departure (edit mode only — view mode never shows draft values), as an
 *  instant, when the date or the time is changed. */
function usePendingStart(bookingId: number, tz: string): number | null {
  const { mode } = useRecordEditor();
  const { state, baselineOf } = useDraft();
  if (mode !== 'edit') return null;
  const rec = `booking:${bookingId}`;
  const entries = state.entries[rec];
  if (!entries?.date && !entries?.departure_time) return null;
  const date = (entries.date?.to ?? baselineOf(rec, 'date')) as string | null;
  const time = (entries.departure_time?.to ?? baselineOf(rec, 'departure_time')) as string | null;
  const w = date && time ? parseWallClock(`${date}T${time}`) : null;
  return w ? zonedWallClockToInstant(w, tz).getTime() : null;
}

export function TimeAxis({ bookingId, timezone }: { bookingId: number; timezone: string }) {
  const nav = useContext(TimelineNavContext);
  const pendingStart = usePendingStart(bookingId, timezone);
  const listId = useId();
  const [listOpen, setListOpen] = useState(false);
  const q = useInfiniteQuery({
    queryKey: bookingActionKeys.timeline(bookingId),
    queryFn: ({ pageParam, signal }) => fetchBookingTimeline(bookingId, pageParam ?? {}, signal),
    initialPageParam: null as { from: string; to: string } | null,
    getNextPageParam: (last) => {
      const to = earlierWindow(last);
      return to ? { from: last.window.from, to } : undefined;
    },
  });
  const timeline = useMemo(() => (q.data ? mergeTimelinePages(q.data.pages) : null), [q.data]);
  const model = useMemo(
    () => (timeline ? timelineModel({ timeline, pendingStart }) : null),
    [timeline, pendingStart],
  );

  // Roving focus over the dots, in time order.
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLElement | null)[]>([]);
  const register = (i: number, el: HTMLElement | null) => {
    refs.current[i] = el;
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!model || model.dots.length === 0) return;
    const last = model.dots.length - 1;
    const next =
      e.key === 'ArrowRight' ? Math.min(last, active + 1)
      : e.key === 'ArrowLeft' ? Math.max(0, active - 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : null;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    refs.current[next]?.focus();
  };

  const tz = timeline?.timezone ?? timezone;
  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
        Imports, changes, deliveries and planned notifications of the booking
        {timeline && timeline.bookings.length > 1 ? ' and its party' : ''} · times in {tz}
        {model?.staged ? ' · each stage has its own scale' : ''}
      </Typography>
      {model && model.dots.length > 0 && (
        <Button
          size="small"
          startIcon={<FormatListBulletedIcon />}
          aria-expanded={listOpen}
          aria-controls={listId}
          onClick={() => setListOpen((o) => !o)}
        >
          {listOpen ? 'Hide the list' : 'Show as a list'}
        </Button>
      )}
    </Box>
  );

  return (
    <BlockRule title="Timeline">
      <Box sx={{ display: 'grid', gap: 1 }} data-time-axis>
        {header}
        {q.isLoading ? (
          <Box role="status" aria-label="Loading the timeline" sx={{ display: 'grid', gap: 0.75 }}>
            {AXIS_LANES.map((l) => (
              <Skeleton key={l.lane} variant="rounded" height={14} />
            ))}
          </Box>
        ) : q.error || !model || !timeline ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void q.refetch()}>
                Retry
              </Button>
            }
          >
            Couldn’t load the timeline.
          </Alert>
        ) : (
          <>
            <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: `${LABEL_PX}px minmax(560px, 1fr)`, minWidth: LABEL_PX + 560 }}>
                {/* Lane names */}
                <Box aria-hidden sx={{ pt: `${BAND_HEAD_PX}px` }}>
                  {AXIS_LANES.map((l) => (
                    <Typography
                      key={l.lane}
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ height: laneHeight(l.lane, model), display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'transparent' }}
                    >
                      {l.label}
                    </Typography>
                  ))}
                </Box>
                {/* The plot */}
                <Box sx={{ position: 'relative' }}>
                  <Box aria-hidden sx={{ position: 'relative', height: BAND_HEAD_PX }}>
                    {model.bands.map((b) => (
                      <Typography
                        key={b.stage}
                        variant="caption"
                        component="span"
                        noWrap
                        sx={{ position: 'absolute', left: axisPct(b.x), width: axisPct(b.width), pl: 0.75, fontWeight: 600, color: 'text.secondary' }}
                      >
                        {b.title}
                      </Typography>
                    ))}
                  </Box>
                  {/* Stage boundaries and now, across every lane. */}
                  <Box aria-hidden sx={{ position: 'absolute', inset: `0 0 ${TICKS_PX}px 0`, pointerEvents: 'none' }}>
                    {model.bands.slice(1).map((b) => (
                      <Box key={b.stage} sx={{ position: 'absolute', left: axisPct(b.x), top: 0, bottom: 0, borderLeft: 1, borderColor: 'divider' }} />
                    ))}
                    {model.now !== null && (
                      <Box data-now sx={{ position: 'absolute', left: axisPct(model.now), top: BAND_HEAD_PX - 4, bottom: 0, ...NOW_RULE_SX }} />
                    )}
                  </Box>
                  <Box role="group" aria-label={`Timeline: ${model.dots.length} items — ← and → move between them`} onKeyDown={onKeyDown}>
                    {AXIS_LANES.map((l) => (
                      <Lane key={l.lane} lane={l.lane} model={model}>
                        {l.lane === 'trip' && <TripLane model={model} />}
                        {l.lane === 'planned' && model.plannedOff && (
                          <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)' }} data-planned-off>
                            None listed — this tenant is on TBEL v1, so there are no planned notifications to show
                          </Typography>
                        )}
                        {l.lane === 'planned' && !model.plannedOff && !model.dots.some((d) => d.lane === 'planned') && (
                          <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)' }}>
                            No planned notifications
                          </Typography>
                        )}
                        {model.dots.map((d, i) =>
                          d.lane === l.lane ? (
                            <Dot
                              key={d.key}
                              dot={d}
                              index={i}
                              active={i === active}
                              register={register}
                              onFocusIndex={setActive}
                              nav={nav}
                              {...(l.lane === 'trip' && model.move ? { top: '13px' } : {})}
                            />
                          ) : null,
                        )}
                      </Lane>
                    ))}
                  </Box>
                  <Box aria-hidden sx={{ position: 'relative', height: TICKS_PX }}>
                    {model.ticks.map((t) => (
                      <Typography
                        key={`${t.x}:${t.label}`}
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{
                          position: 'absolute',
                          top: 2,
                          left: axisPct(t.x),
                          transform: t.x <= 0.001 ? 'none' : t.x >= 0.999 ? 'translateX(-100%)' : 'translateX(-50%)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {t.label}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Legend dots={model.dots} />
            {(model.gaps.length > 0 || q.hasNextPage) && (
              <Box sx={{ display: 'grid', gap: 0.25 }} data-timeline-gaps>
                {model.gaps.map((g) => (
                  <Typography key={g.text} variant="caption" color="text.secondary" component="p" sx={{ m: 0, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                    <InfoOutlinedIcon aria-hidden sx={{ fontSize: 14, mt: '1px' }} />
                    <span>{g.text}</span>
                  </Typography>
                ))}
                {q.hasNextPage && (
                  <Box>
                    <Button size="small" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
                      {q.isFetchingNextPage ? 'Loading earlier…' : 'Load earlier'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            {model.move && (
              <Typography variant="body2" component="p" sx={{ m: 0 }} data-move-note>
                In the draft the departure moves to {model.move.label} {tz}: the planned notifications not sent yet will be
                replanned.
              </Typography>
            )}
            <Collapse in={listOpen}>
              <Box id={listId} sx={{ pt: 0.5 }}>
                <ItemList dots={model.dots} nav={nav} tz={tz} />
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </BlockRule>
  );
}
